import { useEffect, useRef } from "react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "./confirmDialog.scss";

/** course names and the like go into swal's html, so escape them */
export const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
  );

/**
 * A sweetalert confirmation driven by props rather than an imperative call, so
 * it sits in JSX next to the thing it guards.
 *
 * props:
 *   open, title, html, confirmText, cancelText, tone ("ask" | "warn"),
 *   onConfirm, onCancel
 *
 * `html` is a markup string - run anything user supplied through esc() first.
 */
const ConfirmDialog = ({
  open,
  title,
  html,
  confirmText = "Yes, continue",
  cancelText = "Cancel",
  tone = "ask",
  onConfirm,
  onCancel,
}) => {
  // hold the latest callbacks without reopening the alert on every render
  const cbs = useRef({ onConfirm, onCancel });
  cbs.current = { onConfirm, onCancel };

  useEffect(() => {
    if (!open) return;

    let alive = true;

    Swal.fire({
      icon: tone === "warn" ? "warning" : "question",
      title: title,
      html: html || undefined,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      reverseButtons: true,
      focusCancel: true,
      buttonsStyling: false,
      customClass: {
        popup: "ce-swal-popup",
        title: "ce-swal-title",
        htmlContainer: "ce-swal-text",
        confirmButton: `ce-swal-btn ce-swal-confirm ce-swal-${tone}`,
        cancelButton: "ce-swal-btn ce-swal-cancel",
      },
    }).then((res) => {
      if (!alive) return;
      if (res.isConfirmed) cbs.current.onConfirm?.();
      else cbs.current.onCancel?.();
    });

    return () => {
      alive = false;
      // the screen behind it moved on - take the alert with it
      if (Swal.isVisible()) Swal.close();
    };
    // reopening is driven by `open` alone
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return null;
};

export default ConfirmDialog;
